// Amazon Connect public AI chat ("Alex"), the site's support entry point.
// It loads on every page and hides the Zendesk messenger, which Google Tag Manager still injects
// until that tag is removed. The snippet ID below ships in every page by design; the real access
// control is the widget's allowed-domain list in Connect.
// Widget config: connect-support-service/infrastructure/widget-config.md ("Public chat").

const WIDGET_ID = '7cf66c8d-2cd3-4d60-9db6-55ac19e6c8dc';
const SNIPPET_ID =
  'QVFJREFIaWswRHBtVGI4a3NBSmpFY3VBekNNODFFRnFLTmNadnlDblV0U012SE5vNlFISDVvMmVJM1dxUW40Y3ZLUExUTlFHQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNRXovVEROa1o4MUcrNWRUdEFnRVFnQ3ZYN25NL0J5OUFmRXRzOWg4cytKQnFGY09XM2tjUnFwOC9nRGlZMEFyZTdGWXNNRktLTnowSW5Pd2I6OldqWXZsMjhmaWxCUURCZ01QV2lJZlJqQzYxUjRhV2hXMTkrdkxBdnhsd0I3bmtjN0dPanExUmlsN3hBeXdQVEF1RmcrQkhueVMvcUJsNlJpMDhFaHBULzlPNEcxZFRDNHdmL2dEeGRYYlczQ1RwWERXNjRMbE85V2NUcTNiUVBLV2pTZHlxb0o4Y0VPZlMrQUJQMVlJanRWU2pJL1FsQT0=';
const CLIENT_URI =
  'https://monei-support.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js';

// DESIGN.md tokens as hex (the widget accepts hex only).
const TOKENS = {
  foreground: '#0a0a0a',
  mutedForeground: '#565656',
  card: '#ffffff',
  background: '#f3f3f3',
  muted: '#f5f5f5',
  border: '#e5e5e5',
  primary: '#65427e',
  primaryForeground: '#fafafa',
  primarySoft: '#e1d7e8'
};

// The widget chrome has no Catalan; content answers still follow the visitor's language.
const widgetLanguageMap: Record<string, string> = {
  en: 'en_US',
  es: 'es_ES',
  ca: 'es_ES',
  pt: 'pt_PT',
  de: 'de_DE',
  it: 'it_IT',
  fr: 'fr_FR'
};

export const getWidgetLanguage = (language: string): string =>
  widgetLanguageMap[language.split('-')[0] ?? ''] || 'en_US';

// Zendesk must go away entirely, not just visually: its own API is the supported way to remove the
// messenger, and the CSS below covers the case where Zendesk boots after we ask.
const suppressZendesk = () => {
  let attempts = 0;
  const hide = () => {
    const zE = (window as any).zE;
    if (!zE) return false;
    try {
      zE('messenger', 'hide');
    } catch (error) {
      console.error('Zendesk widget error:', error);
    }
    return true;
  };
  if (hide()) return;
  const timer = setInterval(() => {
    attempts += 1;
    if (hide() || attempts > 20) clearInterval(timer);
  }, 500);
};

// How the visitor arrived, not what they are reading: noise in the agent's prompt.
const isTrackingParam = (key: string) =>
  key.startsWith('utm_') || ['gclid', 'fbclid', 'msclkid', '_gl', 'ref'].includes(key);

// The path alone loses what the visitor was actually looking at - a plan, a payment method or a
// filter chosen in the query string. No hash: these attributes are refreshed per page, so an
// in-page anchor clicked afterwards would be reported one section behind.
const pageLocation = () => {
  const kept = new URLSearchParams();
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (!isTrackingParam(key)) kept.append(key, value);
  });
  const search = kept.toString();
  return `${window.location.pathname}${search ? `?${search}` : ''}`;
};

const pageAttributes = (language: string) => ({
  audience: 'public',
  page: pageLocation(),
  pageTitle: document.title.slice(0, 200),
  // The chat flow reads this as `HostedWidget-locale` to pick the greeting language.
  locale: language
});

const callWidget = (...args: unknown[]) => {
  const widget = (window as any).amazon_connect;
  if (!widget) return;
  try {
    widget(...args);
  } catch (error) {
    console.error('Connect chat widget error:', error);
  }
};

const STYLE_ID = 'connect-chat-widget-overrides';
const injectOverrides = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    // Zendesk: the launcher bubble and the messenger window it opens.
    'iframe#launcher,iframe#webWidget{display:none !important;}',
    'div[class*="acWidgetContainer"]{right:10px !important;bottom:10px !important;}',
    // The widget goes full screen below 600px wide or 550px tall, its own breakpoint: the close
    // button becomes a 45px full-width bar above the chat. The floating-panel geometry is scoped
    // to the complement, or the bar shrinks into a square over the chat header and the margin
    // opens a gap under the composer. `.mobile` alone is no signal: `fullscreenMode` sets it on
    // desktops too.
    '#amazon-connect-open-widget-button{width:48px !important;height:48px !important;transition:transform .2s ease;}',
    '@media (min-width:601px) and (min-height:551px){#amazon-connect-close-widget-button{width:48px !important;height:48px !important;transition:transform .2s ease;}}',
    '@media (min-width:601px) and (min-height:551px){#amazon-connect-open-widget-button:hover,#amazon-connect-close-widget-button:hover{transform:scale(1.05);}}',
    '@media (min-width:601px) and (min-height:551px){#amazon-connect-widget-frame{transform-origin:bottom right;margin-bottom:12px !important;}}',
    // Full screen reads as one app screen: the bar takes the header colour, the frame tucks 4px
    // under it to hide the chat header's rounded corners (no option removes them), and the page
    // behind stops scrolling.
    '@media only screen and (max-width:600px),screen and (max-height:550px){#amazon-connect-close-widget-button.mobile{background-color:#65427e !important;box-shadow:none !important;}}',
    '@media only screen and (max-width:600px),screen and (max-height:550px){#amazon-connect-widget-frame.show.mobile{top:41px !important;height:auto !important;border-radius:0 !important;box-shadow:none !important;}}',
    '@media only screen and (max-width:600px),screen and (max-height:550px){html:has(#amazon-connect-widget-frame.show.mobile),html:has(#amazon-connect-widget-frame.show.mobile) body{overflow:hidden !important;}}',
    '#amazon-connect-widget-frame.show{animation:connect-chat-slide-in .25s ease-out;}',
    '@keyframes connect-chat-slide-in{from{opacity:0;transform:translateY(12px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}'
  ].join('\n');
  document.head.appendChild(style);
};

// The chat iframe is same-origin. `focusBorderColor` draws side and bottom borders the unfocused
// composer does not have, so the text box narrows on focus; no widget option avoids it. Keep only
// the top separator and show focus through its colour. Anchored on structure, not hashed classes.
const COMPOSER_STYLE_ID = 'connect-chat-composer-styles';
const styleComposer = () => {
  const frame = document.getElementById(
    'amazon-connect-chat-widget-iframe'
  ) as HTMLIFrameElement | null;
  const doc = frame?.contentDocument;
  if (!doc?.head || doc.getElementById(COMPOSER_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = COMPOSER_STYLE_ID;
  style.textContent = [
    'html body .composer > div{border-width:1px 0 0 !important;border-style:solid !important;border-color:#e5e5e5 !important;}',
    'html body .composer > div:focus-within{border-top-color:#65427e !important;}',
    // iOS zooms the page into any text field under 16px, and the zoom pushes the full-screen chat
    // out of view. Touch devices get a 16px composer, and 16px messages to match (the widget's
    // size variables are not what its elements read). The media query tests the device, not a
    // width: inside the iframe a width measures the 400px desktop panel. Desktop keeps 14px.
    '@media (hover:none) and (pointer:coarse){html body [data-testid="message-body"],html body [data-testid="message-body"] *,html body .composer textarea{font-size:16px !important;}}',
    // A code block wider than the bubble made the message row a horizontal scroller. A wheel or
    // swipe that starts over a horizontal scroller latches onto it, so the transcript stopped
    // scrolling; moving the scroller into the block itself kept the trap. Long lines wrap instead,
    // so nothing scrolls sideways. Code is smaller than prose on every device: one element more
    // specific than the 16px touch rule above, so it wins over it.
    'html body [data-testid="message-body"] pre{white-space:pre-wrap;overflow-wrap:anywhere;}',
    'html body [data-testid="message-body"] pre,html body [data-testid="message-body"] pre *{font-size:12px !important;}',
    'html body [data-testid="message-body"] :not(pre) > code{font-size:.9em !important;}'
  ].join('\n');
  doc.head.appendChild(style);
};

let initialized = false;

// The widget script consumes the command queue AT EVALUATION TIME: the queue and every config
// command must exist BEFORE the script loads.
const start = (language: string) => {
  initialized = true;

  injectOverrides();
  suppressZendesk();

  const w = window as any;
  w.amazon_connect =
    w.amazon_connect ||
    Object.assign(
      function (...args: unknown[]) {
        (w.amazon_connect.ac = w.amazon_connect.ac || []).push(args);
      },
      {ac: [] as unknown[]}
    );

  callWidget('snippetId', SNIPPET_ID);
  callWidget('styles', {
    iconType: 'CHAT',
    openChat: {color: '#ffffff', backgroundColor: '#8961a5'},
    closeChat: {color: '#ffffff', backgroundColor: '#8961a5'}
  });
  callWidget('customStyles', {
    global: {
      frameWidth: '400px',
      frameHeight: '700px',
      textColor: TOKENS.foreground,
      fontSize: '14px',
      typeface: "'Inter', system-ui, sans-serif",
      headerHeight: '38px'
    },
    header: {
      headerTextColor: TOKENS.primaryForeground,
      headerBackgroundColor: TOKENS.primary
    },
    transcript: {
      messageFontSize: '14px',
      widgetBackgroundColor: TOKENS.card,
      agentMessageTextColor: TOKENS.foreground,
      customerMessageTextColor: TOKENS.foreground,
      systemMessageTextColor: TOKENS.mutedForeground,
      agentChatBubbleColor: TOKENS.muted,
      systemChatBubbleColor: TOKENS.background,
      customerChatBubbleColor: TOKENS.primarySoft
    },
    composer: {
      fontSize: '14px',
      textColor: TOKENS.foreground,
      backgroundColor: TOKENS.card,
      borderColor: TOKENS.border,
      focusBorderColor: TOKENS.primary,
      placeholderColor: TOKENS.mutedForeground
    },
    fullscreenMode: true
  });
  callWidget('customizationObject', {
    header: {dropdown: true},
    endChat: {enableConfirmationDialog: true},
    transcript: {eventMessages: {participantJoined: '', participantReturned: ''}},
    // Unlike the merchant chat, no attachments: anonymous uploads from the open internet.
    composer: {alwaysHideToolbar: true, disableEmojiPicker: true, disableCustomerAttachments: true},
    footer: {skipCloseChatButton: true}
  });
  // Stock lifecycle hook: the chat UI is up by the time this fires.
  callWidget('registerCallback', {CONNECTION_ESTABLISHED: styleComposer});
  callWidget('supportedMessagingContentTypes', [
    'text/plain',
    'text/markdown',
    'application/vnd.amazonaws.connect.message.interactive',
    'application/vnd.amazonaws.connect.message.interactive.response'
  ]);
  callWidget('language', getWidgetLanguage(language));
  // No JWT on the public widget, so page context travels as plain contact attributes. Connect
  // prefixes the keys (HostedWidget-*) and they are client-mutable: context only, never identity.
  callWidget('contactAttributes', pageAttributes(language));

  // The client script identifies its widget by the id ON THE SCRIPT TAG - that is how the official
  // snippet does it. Without the id it loads, leaves the command queue untouched and renders
  // nothing, so this cannot use the shared loadScript util, which sets no attributes.
  const script = document.createElement('script');
  script.src = CLIENT_URI;
  script.async = true;
  script.id = WIDGET_ID;
  document.head.appendChild(script);
};

export const initConnectChatWidget = (language: string) => {
  if (typeof window === 'undefined' || initialized) return;
  start(language);
};

// Language and page change as the visitor browses; a chat started later must reflect where they
// actually are.
export const updateConnectChatContext = (language: string) => {
  if (!initialized) return;
  callWidget('language', getWidgetLanguage(language));
  callWidget('contactAttributes', pageAttributes(language));
};
